import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Buildings, UsersThree, ChartBar, CreditCard, SignOut, List, X, Gear, Article } from '@phosphor-icons/react';

const NAV = [
  { to: '/admin', icon: ChartBar, label: 'Platform Overview', end: true },
  { to: '/admin/users', icon: UsersThree, label: 'Users' },
  { to: '/admin/subscriptions', icon: CreditCard, label: 'Subscriptions' },
  { to: '/admin/blog', icon: Article, label: 'Blog' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      <div className="p-4 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-zinc-900 rounded-sm flex items-center justify-center">
            <Gear weight="bold" className="text-white" size={14} />
          </div>
          <div>
            <span className="text-sm font-black tracking-tight block" style={{ fontFamily: 'var(--font-heading)' }}>HostCockpit</span>
            <span className="text-[10px] font-bold text-[#0052FF] uppercase tracking-wider">Admin Panel</span>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-2">
        {NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 mx-2 my-0.5 text-sm font-medium rounded-sm transition-colors ${isActive ? 'bg-zinc-100 text-zinc-950 font-bold' : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'}`}
            data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon size={18} weight="duotone" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center text-xs font-bold text-white">
            {user?.name?.charAt(0)?.toUpperCase() || 'A'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-zinc-900 truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-red-600 transition-colors w-full" data-testid="admin-logout">
          <SignOut size={16} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="admin-layout">
      <aside className="hidden lg:flex w-56 bg-white border-r border-zinc-200 flex-col fixed inset-y-0 left-0 z-30" data-testid="admin-sidebar">
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-56 bg-white flex flex-col h-full">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600"><X size={18} /></button>
            <SidebarContent />
          </aside>
        </div>
      )}
      <main className="flex-1 lg:ml-56">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-6 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-zinc-600" data-testid="admin-mobile-toggle">
            <List size={20} />
          </button>
          <div />
          <div className="flex items-center gap-2 text-xs text-zinc-500 font-medium">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            Platform Admin
          </div>
        </header>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
